import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import { AREAS, IMAGES, METHODS } from "@/data/site";

export function AreasList() {
  return (
    <div data-testid="page-areas" className="page-enter pt-32">
      <SEO
        title="Domaines d'accompagnement — Psychologue Paris 16e"
        description="Anxiété, psychotraumatisme, burn-out, troubles psychosomatiques, séparation : les domaines d'accompagnement à Paris 16e et en visio."
      />
      <section className="container-fxv py-16">
        <Reveal>
          <p className="overline gold-line">Domaines</p>
        </Reveal>
        <Reveal delay={0.05}>
          <h1 className="font-serif text-5xl md:text-6xl text-[#0C2340] mt-6 max-w-3xl leading-[1.05]">
            Les difficultés que nous traversons ensemble
          </h1>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="text-[#4A5568] mt-6 max-w-2xl text-lg leading-relaxed">
            Chacun de ces domaines fait l'objet d'une page dédiée. Vous y
            trouverez les symptômes, les conséquences au quotidien, le
            déroulement de l'accompagnement et les bénéfices attendus.
          </p>
        </Reveal>
      </section>

      <section className="container-fxv pb-24">
        <div className="grid md:grid-cols-2 gap-px bg-[#E2E8F0] border border-[#E2E8F0]">
          {AREAS.map((a, i) => (
            <Reveal key={a.slug} delay={i * 0.05}>
              <Link
                to={`/domaines/${a.slug}`}
                data-testid={`area-list-${a.slug}`}
                className="group bg-white p-10 h-full block hover:bg-[#FAFAFA] transition-colors"
              >
                <p className="overline">{String(i + 1).padStart(2, "0")} · Domaine</p>
                <h2 className="font-serif text-3xl text-[#0C2340] mt-4 leading-snug">
                  {a.title}
                </h2>
                <p className="text-[#4A5568] mt-4 leading-relaxed">{a.intro}</p>
                <span className="mt-6 inline-flex items-center gap-2 text-sm text-[#0C2340] group-hover:text-[#C5A059]">
                  En savoir plus
                  <ArrowRight size={16} strokeWidth={1.5} />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}

export function AreaDetail() {
  const { slug } = useParams();
  const area = AREAS.find((a) => a.slug === slug);

  if (!area) {
    return (
      <div className="pt-40 container-fxv text-center" data-testid="area-not-found">
        <p className="font-serif text-3xl text-[#0C2340]">Page introuvable</p>
        <Link to="/domaines" className="text-[#C5A059] mt-4 inline-block">
          Retour aux domaines
        </Link>
      </div>
    );
  }

  const otherAreas = AREAS.filter((a) => a.slug !== slug).slice(0, 3);

  return (
    <div data-testid={`page-area-${area.slug}`} className="page-enter pt-32">
      <SEO title={area.metaTitle} description={area.metaDescription} />

      <section className="container-fxv py-12">
        <Link
          to="/domaines"
          data-testid="area-back-link"
          className="text-sm text-[#4A5568] hover:text-[#0C2340] inline-flex items-center gap-2"
        >
          <ArrowLeft size={16} strokeWidth={1.5} />
          Tous les domaines
        </Link>
      </section>

      <section className="container-fxv pb-16 grid lg:grid-cols-12 gap-12 items-end">
        <div className="lg:col-span-7">
          <p className="overline gold-line">Domaine d'accompagnement</p>
          <h1 className="font-serif text-5xl md:text-6xl text-[#0C2340] mt-6 leading-[1.05]">
            {area.hero}
          </h1>
          <p className="text-[#4A5568] mt-8 text-lg leading-relaxed max-w-2xl">
            {area.intro}
          </p>
        </div>
        <div className="lg:col-span-5">
          <img
            src={IMAGES.nature}
            alt=""
            className="w-full aspect-[5/4] object-cover"
          />
        </div>
      </section>

      <section className="bg-[#F0F4F8] section-fxv">
        <div className="container-fxv grid lg:grid-cols-2 gap-12">
          <Block title="Quels sont les symptômes ?" items={area.symptomes} />
          <Block
            title="Quelles sont les conséquences au quotidien ?"
            items={area.consequences}
          />
        </div>
      </section>

      <section className="section-fxv bg-white">
        <div className="container-fxv grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="overline gold-line">Accompagnement</p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#0C2340] mt-6 leading-tight">
              Comment se déroule l'accompagnement ?
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <p className="text-lg text-[#4A5568] leading-relaxed">
              {area.deroulement}
            </p>
            <div className="mt-10">
              <p className="overline gold-line">Bénéfices attendus</p>
              <ul className="mt-6 space-y-4">
                {area.benefices.map((b) => (
                  <li
                    key={b}
                    className="flex items-start gap-4 text-[#0C2340]"
                  >
                    <Check
                      size={18}
                      strokeWidth={2}
                      className="text-[#C5A059] mt-1 shrink-0"
                    />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
            <Link
              to="/contact"
              data-testid={`area-cta-${area.slug}`}
              className="mt-10 inline-flex items-center gap-2 rounded-full bg-[#0C2340] text-white px-7 py-3.5 text-sm font-medium hover:bg-[#163861] transition-colors"
            >
              Prendre rendez-vous
              <ArrowRight size={16} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-[#0C2340] text-white section-fxv">
        <div className="container-fxv">
          <div className="max-w-2xl">
            <p className="overline">
              <span className="gold-line">Méthodes mobilisées</span>
            </p>
            <h2 className="font-serif text-4xl md:text-5xl mt-6 text-white">
              Des outils intégratifs adaptés à votre situation
            </h2>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 md:grid-cols-3 gap-4">
            {METHODS.slice(0, 6).map((m) => (
              <Link
                key={m.slug}
                to={`/methodes/${m.slug}`}
                className="border border-[#1A3454] p-6 hover:border-[#C5A059] transition-colors"
              >
                <p className="font-serif text-xl text-white">{m.name}</p>
                <p className="text-sm text-[#C9D4E3] mt-3 leading-relaxed line-clamp-3">
                  {m.objectif}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="section-fxv bg-white">
        <div className="container-fxv">
          <p className="overline gold-line">Autres domaines</p>
          <div className="mt-8 grid md:grid-cols-3 gap-px bg-[#E2E8F0] border border-[#E2E8F0]">
            {otherAreas.map((a) => (
              <Link
                key={a.slug}
                to={`/domaines/${a.slug}`}
                className="bg-white p-8 hover:bg-[#FAFAFA] transition-colors group"
              >
                <p className="font-serif text-xl text-[#0C2340]">{a.title}</p>
                <span className="mt-4 inline-flex items-center gap-2 text-sm text-[#0C2340] group-hover:text-[#C5A059]">
                  Découvrir
                  <ArrowRight size={16} strokeWidth={1.5} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function Block({ title, items }) {
  return (
    <div>
      <h2 className="font-serif text-3xl text-[#0C2340]">{title}</h2>
      <ul className="mt-6 space-y-3">
        {items.map((it) => (
          <li
            key={it}
            className="flex items-start gap-4 text-[#4A5568] leading-relaxed"
          >
            <span className="text-[#C5A059] font-serif text-2xl leading-none mt-0.5">
              ·
            </span>
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
